import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { useAuthContext } from "../../../AuthContextWrapper";
import { OUTCOMES } from "../../../constants";
import { CheckOption, MultiCheckForm } from "../shared/MultiCheckForm";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  inheritingFinancialAccounts = "Inheriting financial accounts",
  rebalancingManagingPortfolio = "Rebalancing and managing a portfolio",
  retirementVsNon = "Retirement versus non-retirement accounts",
  sellingRetitlingHome = "Selling or re-titling a home",
  splittingInheritance = "Distributing/splitting inheritance among beneficiaries",
  somethingElse = "Something else",
}

const somethingElseFollowUpLabel = "Let us know";

export const WhatToDiscussScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext()
  const { latestFinancialConvo, setLatestFinancialConvo } = useContext(
    FinancialConvoContext
  );
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (
    checked: string[],
    checkedFollowUps: string[]
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    const financialConvo = latestFinancialConvo || new FinancialConvo();
    financialConvo.discussionTopics = checked;

    // if somethingElse checked, get the somethingElse text entry
    let somethingElseIndex;
    const somethingElseKey = "somethingElse";
    if (checked.includes(somethingElseKey)) {
      // which checkedFollowUps index will have the text?
      helpOptions.forEach((checkOption: CheckOption, index: number) => {
        if (checkOption.key === somethingElseKey) {
          somethingElseIndex = index;
        }
      });
      if (somethingElseIndex) {
        financialConvo.discussionTopicsSomethingElse =
          checkedFollowUps[somethingElseIndex];
      }
    } else {
      financialConvo.discussionTopicsSomethingElse = "";
    }

    try {
      await financialConvo.saveRequest();
      setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const outcomesDictionary = enumToStringDictionary(Outcomes);
  const helpOptions: CheckOption[] = [];

  Object.keys(outcomesDictionary).forEach((key: string) => {
    const value = outcomesDictionary[key];
    helpOptions.push({
      key,
      value,
      followUp:
        value === Outcomes.somethingElse
          ? somethingElseFollowUpLabel
          : undefined,
    });
  });
  const initFollowups = helpOptions.map((checkOption: CheckOption) =>
    latestFinancialConvo?.discussionTopicsSomethingElse &&
    checkOption.value === Outcomes.somethingElse
      ? latestFinancialConvo.discussionTopicsSomethingElse
      : ""
  );

  return currentScreen ? (
    <FlowScreenWrapper
      title="Anything specific that you’d like to discuss with a financial advisor?"
      subTitle="Feel free to select as many options as you’d like."
    >
      <MultiCheckForm
        initChecked={latestFinancialConvo?.discussionTopics}
        initFollowUps={initFollowups}
        options={helpOptions}
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
        followUpsRows={6}
        followUpsCharLimit={800}
      />
      {/* <p>test</p> */}
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
